import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { toast } from 'react-toastify';
import {
  RadioButtonChecked, RadioButtonUnchecked,
  ArrowBack as ArrowBackIcon,
  Link as ShareLinkIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { Page } from 'Components';
import { ConfirmDialog } from 'Components/ConfirmDialog';
import { axios, endpoints } from 'Api';
import {
  listContributions, listLinks, listContributorsByLink, reactivateContributionLink,
} from 'Api/apiFunctions';
import { resolveContributionTitle } from 'Utils/contributionTitle';
import { SHARED_FEATURE_ENABLED } from 'Config/featureFlags';
import AlterImage from 'Assets/no-image-available.png';
import LinkFolderCard from 'Pages/private/videos/components/LinkFolderCard';
import PublishSettingsModal from 'Pages/private/videos/components/PublishSettingsModal';
import UploadLinkModal from 'Components/UploadDropdown/UploadLinkModal';
import useFolderCovers from 'Pages/private/videos/hooks/useFolderCovers';
import videoStyles from 'Pages/private/videos/index.module.css';
import layoutStyles from '../../content-layout-details/index.module.css';
import { SharedSidebarV2 } from './SharedSidebarV2';

const EMPTY_LINKS = [];

// Same content this feature already shows on the classic /your-videos "Shared"
// tab (tabTitle === 2) — ported here so the V2 pill's "Shared" tab uses the
// real contribution-links data instead of the "Coming soon" placeholder.
// Publish-to-library / edit-folder / invite-as-viewer are intentionally left
// on the classic page for now; this is the browsing experience.

const changeDateFormat = (date) => {
  if (!date) return date;
  const [year, month, day] = date.split('-');
  return `${month}/${day}/${year}`;
};

const getContributionThumbnail = (contrib) => {
  if (contrib.type === 'album') {
    const iconSigned = contrib.payload?.payload?.icon_signed;
    if (iconSigned) return iconSigned;
    const icon = contrib.payload?.payload?.icon;
    if (icon) return icon.startsWith('http') ? icon : `https://d1lnmqwvmebyqc.cloudfront.net/${icon}`;
  } else if (contrib.type === 'video') {
    const thumbnailSigned = contrib.payload?.payload?.thumbnail_signed;
    if (thumbnailSigned) return thumbnailSigned;
    const thumbnailsSigned = contrib.payload?.payload?.thumbnails_signed;
    if (thumbnailsSigned && thumbnailsSigned.length > 0) return thumbnailsSigned[0];
  }
  return AlterImage;
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 'revoked' | 'expired' | 'active'
const getFolderStatus = (link) => {
  if (!link) return 'active';
  if (link.is_active === '0' || link.is_active === 0 || link.is_active === false) return 'revoked';
  if (link.is_expired === true || link.is_expired === 1 || link.is_expired === '1') return 'expired';
  return 'active';
};

export const SharedTab = ({ headerLeading = null }) => {
  const [links, setLinks] = useState([]);
  const [fetchingLinks, setFetchingLinks] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [fetchingContributions, setFetchingContributions] = useState(false);
  const [contributors, setContributors] = useState([]);
  const [sidebarContributor, setSidebarContributor] = useState(null);
  const [filteringContributions, setFilteringContributions] = useState(false);
  const [copiedFolderLink, setCopiedFolderLink] = useState(false);
  const [showReactivatePopup, setShowReactivatePopup] = useState(false);
  const [reactivatingFolder, setReactivatingFolder] = useState(false);
  const [selectedContributions, setSelectedContributions] = useState([]);
  const [publishingContributions, setPublishingContributions] = useState(false);
  const [showPublishSettingsModal, setShowPublishSettingsModal] = useState(false);
  // "Create folder" from the empty Shared Folders sidebar → opens the shared
  // folder (upload link) modal straight into its create view.
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);

  const fetchLinksList = useCallback(async () => {
    setFetchingLinks(true);
    try {
      const response = await listLinks({ status: 'all' });
      setLinks(response?.data?.success ? (response.data.links || []) : []);
    } catch (error) {
      setLinks([]);
    } finally {
      setFetchingLinks(false);
    }
  }, []);

  useEffect(() => {
    if (SHARED_FEATURE_ENABLED) fetchLinksList();
  }, [fetchLinksList]);

  // Cover thumbnails for the folder grid — steals the first usable thumbnail
  // out of each folder's own contributions (see useFolderCovers for why).
  const fetchCoverContributions = useCallback(async (uuid) => {
    try {
      const response = await listContributions({ filter_by: 'uuid', value: uuid, uuid });
      return response?.data?.success ? (response.data.contributions || []) : [];
    } catch (error) {
      return [];
    }
  }, []);
  const coverLinks = !selectedLink ? links : EMPTY_LINKS;
  const { covers: folderCovers } = useFolderCovers(coverLinks, fetchCoverContributions);

  const fetchFolderContributions = async (link) => {
    if (!link?.uuid) return;
    setFetchingContributions(true);
    try {
      const response = await listContributions({ filter_by: 'uuid', value: link.uuid, uuid: link.uuid });
      setContributions(response?.data?.success ? (response.data.contributions || []) : []);
    } catch (error) {
      setContributions([]);
    } finally {
      setFetchingContributions(false);
    }
  };

  const fetchFolderContributors = async (link) => {
    if (!link?.uuid) return;
    try {
      const response = await listContributorsByLink({ uuid: link.uuid });
      setContributors(response?.data?.success ? (response.data.contributors || []) : []);
    } catch (error) {
      setContributors([]);
    }
  };

  const handleLinkSelect = async (link) => {
    setSelectedLink(link);
    setSidebarContributor(null);
    setContributions([]);
    setContributors([]);
    setSelectedContributions([]);
    if (link) {
      fetchFolderContributors(link);
      await fetchFolderContributions(link);
    }
  };

  // Right-sidebar filter — null = all contributors, else just theirs.
  const handleSidebarContributorSelect = async (contributor) => {
    setSidebarContributor(contributor);
    setSelectedContributions([]);
    if (!selectedLink) return;
    setFilteringContributions(true);
    try {
      const filterBy = contributor
        ? (contributor.contributor_email ? 'email' : 'uuid')
        : 'uuid';
      const value = contributor
        ? (contributor.contributor_email || contributor.uuid)
        : selectedLink.uuid;
      const response = await listContributions({ filter_by: filterBy, value, uuid: selectedLink.uuid });
      setContributions(response?.data?.success ? (response.data.contributions || []) : []);
    } catch (error) {
      setContributions([]);
    } finally {
      setFilteringContributions(false);
    }
  };

  // Contribution selection (for publishing) + the derived unpublished set.
  const unpublishedContributions = contributions.filter(
    (c) => !c.connected_source || c.connected_source === '',
  );
  const isContributionSelected = (contributionId) => selectedContributions.includes(contributionId);
  const toggleContributionSelection = (contributionId) => {
    setSelectedContributions((prev) => (prev.includes(contributionId)
      ? prev.filter((id) => id !== contributionId)
      : [...prev, contributionId]));
  };
  const toggleSelectAllContributions = () => {
    const unpublishedIds = unpublishedContributions.map((c) => c.id);
    setSelectedContributions((prev) => (
      prev.length === unpublishedIds.length ? [] : unpublishedIds
    ));
  };

  // Publish selected contributions with settings from PublishSettingsModal —
  // same publish logic (addVideo / createAndAttachAlbum) as the classic tab.
  const publishSelectedContributions = async (settings) => {
    if (selectedContributions.length === 0) return;
    setPublishingContributions(true);
    setShowPublishSettingsModal(false);
    const selectedItems = contributions.filter((c) => selectedContributions.includes(c.id));
    const useAdvanced = settings?.useAdvanced && settings?.items;

    try {
      const results = await Promise.all(
        selectedItems.map(async (contrib) => {
          const { payload } = contrib.payload;
          const contribKey = contrib.id || contrib.uuid;
          const itemSettings = useAdvanced
            ? settings.items.find((item) => item.uniqueKey === contribKey)
            : null;

          if (contrib.type === 'video') {
            const formData = new FormData();
            formData.append('contribution_id', contrib.id || '');
            formData.append('uuid', contrib.uuid || '');
            formData.append('draft_id', payload.draft_id || '');
            formData.append('title', itemSettings?.title || payload.title || '');

            if (itemSettings) {
              if (itemSettings.selectedCategory?.type === 'id' && itemSettings.selectedCategory?.value) {
                formData.append('category_id', itemSettings.selectedCategory.value);
                formData.append('category_name', '');
              } else {
                formData.append('category_id', '');
                formData.append('category_name', itemSettings.selectedCategory?.name || payload.category_name || '');
              }
            } else if (settings?.video?.category_id) {
              formData.append('category_id', settings.video.category_id);
              formData.append('category_name', '');
            } else {
              formData.append('category_id', '');
              formData.append('category_name', settings?.video?.category_name || payload.category_name || '');
            }

            formData.append('file_name', payload.file_name || '');
            formData.append('file_key', payload.file_key || '');
            formData.append('document_id', payload.document_id || '');
            formData.append('bucket_folder_path', payload.bucket_folder_path || payload.file_key || '');
            formData.append('description', itemSettings?.description || payload.description || '');
            formData.append('video_size', payload.video_size || '');

            let videoGroupIds;
            if (itemSettings?.visibility === '3' && itemSettings?.selectedGroups?.length > 0) {
              videoGroupIds = itemSettings.selectedGroups.join(',');
            } else if (settings?.video?.group_ids?.length > 0) {
              videoGroupIds = settings.video.group_ids.join(',');
            } else {
              const existingIds = payload.group_ids;
              if (Array.isArray(existingIds)) {
                videoGroupIds = existingIds.join(',');
              } else if (typeof existingIds === 'string' && existingIds !== '[]') {
                videoGroupIds = existingIds.replace(/^\[|\]$/g, '');
              } else {
                videoGroupIds = '';
              }
            }
            formData.append('group_ids', videoGroupIds);
            formData.append('visibility', itemSettings?.visibility || settings?.video?.visibility || payload.visibility || '1');
            formData.append('subcategory_id', payload.subcategory_id || '');
            formData.append('playlist_ids', payload.playlist_ids || '');
            formData.append('videoHeight', payload.videoHeight || '');
            formData.append('videoWidth', payload.videoWidth || '');
            formData.append('videoOreintation', payload.videoOreintation || '');
            formData.append('s3_status', 'completed');

            const selectedThumbnail = itemSettings?.thumbnail || payload.thumbnail;
            if (selectedThumbnail) {
              formData.append('thumbnails_s3_path', selectedThumbnail);
            }
            if (payload.thumbnails && payload.thumbnails.length > 0) {
              formData.append('thumbnail_list_s3_paths', JSON.stringify(payload.thumbnails));
            }

            const response = await axios.post(endpoints.addVideo, formData);
            return { success: response?.data?.success, type: 'video', title: itemSettings?.title || payload.title };
          }

          if (contrib.type === 'album') {
            const photosData = (payload.photos || []).map((photo) => ({
              path: photo.path,
              size: String(photo.size || '0'),
              name: photo.name || '',
            }));

            let albumGroupIds;
            if (itemSettings?.visibility === '3' && itemSettings?.selectedGroups?.length > 0) {
              albumGroupIds = itemSettings.selectedGroups.join(',');
            } else if (settings?.album?.group_ids?.length > 0) {
              albumGroupIds = settings.album.group_ids.join(',');
            } else {
              const existingIds = payload.group_ids;
              if (Array.isArray(existingIds)) {
                albumGroupIds = existingIds.join(',');
              } else if (typeof existingIds === 'string' && existingIds !== '[]') {
                albumGroupIds = existingIds.replace(/^\[|\]$/g, '');
              } else {
                albumGroupIds = '';
              }
            }

            const albumPayload = {
              contribution_id: contrib.id || '',
              uuid: contrib.uuid || '',
              title: itemSettings?.title || payload.title || '',
              description: itemSettings?.description || payload.description || '',
              visibility: itemSettings?.visibility || settings?.album?.visibility || payload.visibility || '2',
              group_id: albumGroupIds,
              icon: payload.icon || '',
              photos: photosData,
            };

            const response = await axios.post(endpoints.createAndAttachAlbum, albumPayload);
            return { success: response?.data?.success, type: 'album', title: itemSettings?.title || payload.title };
          }

          return { success: false, type: contrib.type, title: payload?.title };
        }),
      );

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.length - successCount;
      if (successCount > 0) toast.success(`Successfully published ${successCount} item(s)`);
      if (failCount > 0) toast.error(`Failed to publish ${failCount} item(s)`);

      setSelectedContributions([]);
      if (sidebarContributor) {
        handleSidebarContributorSelect(sidebarContributor);
      } else if (selectedLink) {
        fetchFolderContributions(selectedLink);
      }
    } catch (error) {
      toast.error('Failed to publish contributions');
    } finally {
      setPublishingContributions(false);
    }
  };

  const copyFolderLinkToClipboard = async (link) => {
    const url = link?.short_link || link?.link || '';
    if (!url) {
      toast.error('No share link available for this folder');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedFolderLink(true);
      setTimeout(() => setCopiedFolderLink(false), 2000);
      toast.success('Folder link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyFolderLink = () => {
    if (getFolderStatus(selectedLink) === 'revoked') {
      setShowReactivatePopup(true);
      return;
    }
    copyFolderLinkToClipboard(selectedLink);
  };

  const handleReactivateAndCopy = async () => {
    if (!selectedLink?.uuid) return;
    setReactivatingFolder(true);
    try {
      const response = await reactivateContributionLink({ uuid: selectedLink.uuid });
      if (response?.data?.success === false) {
        toast.error(response?.data?.message || 'Failed to reactivate link');
        return;
      }
      const updatedLink = { ...selectedLink, is_active: '1', is_expired: false };
      setSelectedLink(updatedLink);
      setLinks((prev) => prev.map((l) => (l.uuid === updatedLink.uuid ? { ...l, is_active: '1', is_expired: false } : l)));
      await copyFolderLinkToClipboard(updatedLink);
      setShowReactivatePopup(false);
    } catch (error) {
      toast.error('Failed to reactivate link');
    } finally {
      setReactivatingFolder(false);
    }
  };

  if (!SHARED_FEATURE_ENABLED) {
    return (
      <div className={videoStyles.emptyState}>
        <p>Shared folders aren&apos;t enabled for this account yet.</p>
      </div>
    );
  }

  return (
    <Page noHeader={false} slimV2 className={layoutStyles.page} contentClassName={cn(layoutStyles.pageContent, layoutStyles.pcHeaderPad)}>
      <div className={layoutStyles.mainLayout}>
        <div className={layoutStyles.leftSidebar}>
          <SharedSidebarV2
            links={links}
            selectedLink={selectedLink}
            onLinkSelect={handleLinkSelect}
            loading={fetchingLinks}
            folderCovers={folderCovers}
            onCreateFolder={() => setShowCreateFolderModal(true)}
          />
        </div>
        <div className={layoutStyles.rightContent}>
          {/* Mode switcher (Videos / Photo albums / Shared) stays visible
              here regardless of folder selection, matching the other tabs. */}
          <div className={videoStyles.sharedBackBar}>
            {selectedLink && (
              <button
                type="button"
                className={videoStyles.sharedBackBtn}
                onClick={() => handleLinkSelect(null)}
                aria-label="Back to folders"
              >
                <ArrowBackIcon className={videoStyles.icon} />
              </button>
            )}
            {headerLeading}
            {/* {selectedLink && (
              <span className={videoStyles.sharedBreadcrumb}>{selectedLink.label || 'Folder'}</span>
            )} */}
            {selectedLink && (
              <div className={videoStyles.sharedBackBarActions} style={{ marginLeft: 'auto' }}>
                {unpublishedContributions.length > 0 && (
                  <>
                    <button
                      type="button"
                      className={videoStyles.selectAllBtn}
                      onClick={toggleSelectAllContributions}
                    >
                      {selectedContributions.length === unpublishedContributions.length && unpublishedContributions.length > 0 ? (
                        <>
                          <RadioButtonChecked className={videoStyles.icon} />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <RadioButtonUnchecked className={videoStyles.icon} />
                          Select All
                        </>
                      )}
                    </button>
                    {selectedContributions.length > 0 && (
                      <button
                        type="button"
                        className={videoStyles.publishBtn}
                        onClick={() => setShowPublishSettingsModal(true)}
                        disabled={publishingContributions}
                      >
                        {publishingContributions && (
                          <CircularProgress size={16} style={{ color: '#fff' }} />
                        )}
                        {`Publish (${selectedContributions.length})`}
                      </button>
                    )}
                  </>
                )}
                {getFolderStatus(selectedLink) !== 'expired' && (
                  <button
                    type="button"
                    className={cn(videoStyles.copyLinkBtn, copiedFolderLink && videoStyles.copied)}
                    onClick={handleCopyFolderLink}
                    title="Copy shared folder link"
                  >
                    <ShareLinkIcon className={videoStyles.icon} />
                    {copiedFolderLink ? 'Copied!' : 'Copy Link'}
                  </button>
                )}
              </div>
            )}
          </div>

          <div style={{
            flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
          }}
          >
          {fetchingLinks || fetchingContributions ? (
            <div className={videoStyles.loadingGrid}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className={videoStyles.skeletonCard}>
                  <div className={videoStyles.skeletonThumbnail} />
                  <div className={videoStyles.skeletonText} />
                  <div className={videoStyles.skeletonMeta} />
                </div>
              ))}
            </div>
          ) : !selectedLink ? (
            links.length > 0 ? (
              <div className={videoStyles.contributorFoldersGrid}>
                {links.map((link, index) => (
                  <LinkFolderCard
                    key={link.uuid || link.id || index}
                    link={link}
                    onClick={handleLinkSelect}
                    coverUrl={folderCovers[link.uuid]}
                  />
                ))}
              </div>
            ) : (
              <div className={videoStyles.emptyState}><p>No folders found</p></div>
            )
          ) : (
            <div className={videoStyles.contributionsLayout}>
              <div
                className={videoStyles.contributionsMain}
                style={filteringContributions ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
              >
                {contributions.length === 0 ? (
                  <div className={videoStyles.emptyState}>
                    <p>{sidebarContributor ? 'No contributions from this contributor yet' : 'No contributions found'}</p>
                  </div>
                ) : (
                  <div className={`${videoStyles.videoGrid} ${videoStyles.contributionsGrid}`}>
                    {contributions.map((contrib, index) => {
                      const thumbnail = getContributionThumbnail(contrib);
                      const { type } = contrib;
                      const createdAt = contrib.created_at?.split(' ')[0];
                      const formattedDate = changeDateFormat(createdAt);
                      const displayTitle = resolveContributionTitle(contrib.payload?.payload?.title);
                      const typeLabel = type === 'album' ? 'Album' : 'Video';
                      const duration = contrib.payload?.payload?.duration;
                      const videoSize = contrib.payload?.payload?.video_size;
                      const photosCount = contrib.payload?.payload?.photos?.length;
                      const isPublished = contrib.connected_source && contrib.connected_source !== '';
                      const isSelected = isContributionSelected(contrib.id);
                      const contributorLabel = !sidebarContributor
                        ? (contrib.contributor_name || contrib.contributor_email)
                        : null;

                      return (
                        <div
                          key={contrib.id || index}
                          className={cn(videoStyles.videoCard, isSelected && videoStyles.selectedCard, isPublished && videoStyles.publishedCard)}
                          onClick={() => !isPublished && toggleContributionSelection(contrib.id)}
                          onKeyDown={(e) => {
                            if (!isPublished && (e.key === 'Enter' || e.key === ' ')) toggleContributionSelection(contrib.id);
                          }}
                          role="button"
                          tabIndex={0}
                          style={{ cursor: isPublished ? 'default' : 'pointer' }}
                        >
                          <div className={videoStyles.thumbnailContainer}>
                            <img
                              src={thumbnail}
                              alt={displayTitle || typeLabel}
                              className={videoStyles.thumbnail}
                              onError={(e) => { e.target.src = AlterImage; }}
                            />
                            {isPublished ? (
                              <div className={videoStyles.publishedBadge}>Published</div>
                            ) : (
                              <div className={videoStyles.selectionCheckbox}>
                                {isSelected ? (
                                  <RadioButtonChecked style={{ color: '#5aa5ef' }} />
                                ) : (
                                  <RadioButtonUnchecked style={{ color: '#fff' }} />
                                )}
                              </div>
                            )}
                            <div className={videoStyles.typeBadge}>{typeLabel}</div>
                            {duration && (
                              <div className={videoStyles.durationBadge}>{formatDuration(duration)}</div>
                            )}
                            {type === 'album' && photosCount > 0 && (
                              <div className={videoStyles.photosCountBadge}>
                                {`${photosCount} ${photosCount === 1 ? 'photo' : 'photos'}`}
                              </div>
                            )}
                          </div>
                          <div className={cn(videoStyles.cardContent, videoStyles.contributionCardContent)}>
                            {displayTitle && (
                              <h3 style={{
                                margin: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}
                              >
                                {displayTitle}
                              </h3>
                            )}
                            <div className={videoStyles.cardMeta}>
                              {formattedDate && <span className={videoStyles.cardDate}>{formattedDate}</span>}
                              {videoSize && <span className={videoStyles.cardSize}>{`${videoSize} MB`}</span>}
                            </div>
                            {contributorLabel && (
                              <div className={videoStyles.cardMeta}>
                                <span className={videoStyles.cardDate}>{contributorLabel}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {contributors.length > 0 && (
                <aside className={videoStyles.contributorSidebar}>
                  <div className={videoStyles.contributorSidebarTitle}>Contributors</div>
                  <div className={cn(videoStyles.contributorSidebarItem, !sidebarContributor && videoStyles.contributorSidebarItemActive)}>
                    <button
                      type="button"
                      className={videoStyles.contributorSidebarSelect}
                      onClick={() => handleSidebarContributorSelect(null)}
                    >
                      <span className={videoStyles.contributorSidebarName}>All contributors</span>
                    </button>
                  </div>
                  {contributors.map((c, i) => {
                    const keyOf = (x) => (x && (x.contributor_email || x.uuid)) || '';
                    const name = c.contributor_name || c.contributor_email || 'Unknown';
                    const count = Number(c.contribution_count) || 0;
                    const active = !!sidebarContributor && keyOf(c) === keyOf(sidebarContributor);
                    return (
                      <div
                        key={keyOf(c) || i}
                        className={cn(videoStyles.contributorSidebarItem, active && videoStyles.contributorSidebarItemActive)}
                      >
                        <button
                          type="button"
                          className={videoStyles.contributorSidebarSelect}
                          onClick={() => handleSidebarContributorSelect(c)}
                          title={name}
                        >
                          <span className={videoStyles.contributorSidebarName}>{name}</span>
                          {count > 0 && <span className={videoStyles.contributorSidebarCount}>{count}</span>}
                        </button>
                      </div>
                    );
                  })}
                </aside>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showReactivatePopup}
        title="Reactivate this folder link?"
        description="This link was revoked. Reactivating it will make it shareable again, then copy it to your clipboard."
        confirmLabel="Reactivate & Copy"
        onConfirm={handleReactivateAndCopy}
        onCancel={() => setShowReactivatePopup(false)}
        loading={reactivatingFolder}
      />

      <PublishSettingsModal
        open={showPublishSettingsModal}
        onClose={() => setShowPublishSettingsModal(false)}
        onPublish={publishSelectedContributions}
        selectedItems={contributions.filter((c) => selectedContributions.includes(c.id))}
        isPublishing={publishingContributions}
      />

      {/* Create Shared Folder — opened from the empty sidebar's "Create folder"
          button. With no existing links the modal lands on its create view.
          Refresh the folder list after a successful create. */}
      <UploadLinkModal
        open={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onSuccess={() => { setShowCreateFolderModal(false); fetchLinksList(); }}
      />
    </Page>
  );
};

export default SharedTab;
